import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { mainActions } from "../store";
import { useSavedCharts } from "../accounts/SavedChartsProvider";
import classes from "./ChartWorkflow.module.css";

const steps = ["Create", "Refine", "Preview", "Publish"];
const changedPositions = state => {
  const original = state.createdData.map(position => ({ notesPerString: position.chartData.map(string => string.length), notesOffset: position.notesOffset }));
  const refined = state.refineData.perChartData.map(chart => ({ notesPerString: chart.notesPerString, notesOffset: chart.notesOffset }));
  return state.refineData.perChartData.some(chart => chart.omittedNotes?.length) || JSON.stringify(original) !== JSON.stringify(refined);
};

const useWorkflow = step => {
  const dispatch = useDispatch();
  const canRefine = useSelector(state => state.createdData.length > 0);
  const canPreview = useSelector(state => state.publishedData.length > 0);
  const queryString = useSelector(state => state.queryString);
  const changed = useSelector(changedPositions);
  const goTo = target => {
    if (target === step) return;
    if (target === 1 && changed && !window.confirm("Returning to Create will rebuild the chart positions and remove your fingering edits. Continue?")) return;
    if (target === 2 && !canRefine || target >= 3 && !canPreview) return;
    if (target === 4) {
      if (queryString) window.open(`/publish?${queryString}`, "_blank", "noopener");
    } else dispatch(mainActions.setMenuSelected(target));
  };
  return { goTo, canRefine, canPreview, canPublish: canPreview && !!queryString };
};

export const ChartWorkflow = ({ step }) => {
  const { goTo, canRefine, canPreview, canPublish } = useWorkflow(step);
  const saved = useSavedCharts();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  return <div className={classes.workflow}>
    <div className={classes.workflowTop}>
      <ol aria-label="Chart creation steps" data-tour-target={step === 1 ? "create-workflow" : step === 2 ? "refine-workflow" : undefined} className={classes.steps}>
        {steps.map((name, index) => <li key={name} aria-current={index + 1 === step ? "step" : undefined}>
          <button type="button" aria-label={index === 3 ? "Publish — open printable chart in a new tab" : name}
            data-tour-target={step === 2 && index === 2 ? "refine-preview" : undefined}
            disabled={index + 1 === step || index === 1 && !canRefine || index === 2 && !canPreview || index === 3 && !canPublish}
            onClick={() => goTo(index + 1)}>
            <span className={classes.number} aria-hidden="true">{index + 1}</span><span className={classes.stepName}>{name}{index === 3 && <small aria-hidden="true"> ↗</small>}</span>
          </button>
        </li>)}
      </ol>
      <button type="button" className={classes.newChart} disabled={saved.busy} onClick={() => {
        if (!window.confirm("Start a new chart set? Unsaved changes will be lost. Charts already saved in My Charts will be kept.")) return;
        if (saved.newChart) saved.newChart();
        else dispatch(mainActions.startNewChart());
        navigate("/", { replace: true });
      }}>New chart set</button>
    </div>
    <p>{step === 1 ? "Choose your tuning, scale and layout to create your charts."
      : step === 2 ? "Add a title and introduction, then adjust each chart."
        : "Check your finished charts before opening the printable page."}</p>
  </div>;
};

export const WorkflowActions = ({ step }) => {
  const { goTo, canRefine, canPreview, canPublish } = useWorkflow(step);
  return <div className={classes.actions}>
    {step === 3 && <p className={classes.explanation}>Publish opens your printable, shareable chart in a new tab. Save its link or print it from your browser.</p>}
    <div className={classes.buttons}>
      {step > 1 && <button type="button" className={classes.back} onClick={() => goTo(step - 1)}>
        Back to {steps[step - 2]}
      </button>}
      <button type="button" className={classes.next} data-tour-target={step === 1 ? "create-refine" : undefined} disabled={step === 1 ? !canRefine : step === 2 ? !canPreview : !canPublish}
        onClick={() => goTo(step + 1)}>{step < 3 ? `Continue to ${steps[step]}` : "Open printable chart"}</button>
    </div>
    {step === 2 && <p className={classes.note}>Returning to Create rebuilds the chart positions from your scale and layout choices.</p>}
  </div>;
};
